import { useEffect, useRef } from 'react';

import { useEventCallback, useForceUpdate } from '@spazious/react-hooks';

/**
 * Returns the current value of the given CSS variable.
 *
 */
export const useStyleVars = (...styleVars: string[]): (string | undefined)[] => {
  const forceUpdate = useForceUpdate();

  const styleVarsRef = useRef(styleVars);
  const styleVarValuesRef = useRef(new Array(styleVars.length).fill(undefined));

  styleVarsRef.current = styleVars;

  const checkAndSet = useEventCallback((target: Element) => {
    let updated = false;
    const { current: styleVars } = styleVarsRef;
    const { current: styleVarValues } = styleVarValuesRef;
    styleVars.forEach((styleVar, idx) => {
      const currentValue = getComputedStyle(target).getPropertyValue(styleVar);

      if (currentValue !== styleVarValues[idx]) {
        styleVarValues[idx] = currentValue;
        updated = true;
      }
    });

    return updated;
  });

  useEffect(() => {
    let updated = false;

    if (checkAndSet(document.documentElement as Element)) {
      updated = true;
    }

    if (updated) {
      forceUpdate();
    }
  }, [checkAndSet, forceUpdate]);

  useEffect(() => {
    let updated = false;
    // TODO: Lift up MutationObserver to a higher level so that it can be shared between all components that use this hook.
    const styleObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          if (checkAndSet(mutation.target as Element)) {
            updated = true;
          }
        }
      });

      if (updated) {
        forceUpdate();
      }
    });

    styleObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      styleObserver.disconnect();
    };
  }, [checkAndSet, forceUpdate]);

  return styleVarValuesRef.current;
};
